import Layout from '@/components/feature/Layout';
import SEOMeta from '@/components/base/SEOMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { useMemo } from 'react';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
      .slice(0, 3);
  }, [article]);

  const articleSchema = article
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        image: article.coverImage,
        author: {
          '@type': 'Person',
          name: article.author,
        },
        datePublished: article.publishedAt,
        publisher: {
          '@type': 'Organization',
          name: '凤楼信息',
          logo: {
            '@type': 'ImageObject',
            url: 'https://www.fenglou-info.com/logo.png',
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://www.fenglou-info.com/article/${article.id}`,
        },
      }
    : undefined;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.fenglou-info.com' },
      ...(article
        ? [
            {
              '@type': 'ListItem',
              position: 2,
              name: article.category,
              item: `https://www.fenglou-info.com/category/${article.categorySlug}`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: article.title,
            },
          ]
        : []),
    ],
  };

  return (
    <Layout>
      <SEOMeta
        title={article ? article.title : '文章详情 - 凤楼信息'}
        description={article?.excerpt || '查看文章详情'}
        keywords={article?.tags?.join(',') || '凤楼信息'}
        ogType="article"
        ogImage={article?.coverImage}
        canonicalUrl={article ? `https://www.fenglou-info.com/article/${article.id}` : undefined}
        schema={article ? { ...articleSchema, ...breadcrumbSchema } : undefined}
      />

      <div className="pt-24 pb-14 md:pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(article ? [{ label: article.category, href: `/category/${article.categorySlug}` }] : []),
              { label: article?.title || '文章详情' },
            ]}
          />

          {article ? (
            <article className="mt-8">
              <div className="mb-6">
                <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4 whitespace-nowrap">
                  {article.category}
                </span>
                <h1
                  className="text-2xl md:text-4xl font-bold text-foreground-900 leading-tight"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
                  <span className="whitespace-nowrap flex items-center gap-1">
                    <i className="ri-user-line"></i> {article.author}
                  </span>
                  <span className="whitespace-nowrap flex items-center gap-1">
                    <i className="ri-calendar-line"></i> {article.publishedAt}
                  </span>
                  <span className="whitespace-nowrap flex items-center gap-1">
                    <i className="ri-time-line"></i> {article.readTime} 分钟阅读
                  </span>
                  <span className="whitespace-nowrap flex items-center gap-1">
                    <i className="ri-eye-line"></i> {article.views.toLocaleString()} 阅读
                  </span>
                </div>
              </div>

              <div className="rounded-xl overflow-hidden aspect-[16/9] mb-8">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose prose-lg max-w-none text-foreground-700 leading-relaxed
                  prose-h2:text-foreground-900 prose-h2:text-xl prose-h2:font-bold prose-h2:mt-8 prose-h2:mb-4
                  prose-p:leading-relaxed prose-p:mb-4
                  prose-strong:text-foreground-900"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="flex flex-wrap items-center gap-2 mt-10 pt-8 border-t border-background-200">
                <span className="text-sm text-foreground-500 whitespace-nowrap">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center justify-between mt-8 pt-8 border-t border-background-200">
                <Link
                  to="/"
                  className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i> 返回首页
                </Link>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-foreground-400 whitespace-nowrap">分享：</span>
                  <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap" aria-label="分享到微信">
                    <i className="ri-wechat-line"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap" aria-label="分享到微博">
                    <i className="ri-weibo-line"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap" aria-label="复制链接">
                    <i className="ri-link"></i>
                  </button>
                </div>
              </div>
            </article>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto rounded-2xl bg-background-100 text-foreground-400 mb-4">
                <i className="ri-file-search-line text-2xl"></i>
              </div>
              <p className="text-foreground-500 mb-4">文章未找到</p>
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i> 返回首页
              </Link>
            </div>
          )}

          {relatedArticles.length > 0 && (
            <section className="mt-14">
              <h3
                className="text-xl font-bold text-foreground-900 mb-6"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                相关推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} variant="compact" />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </Layout>
  );
}